import { useEffect, useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useSearchParams } from "react-router-dom";
import { businessListingsApi } from "../../lib/businessListings";
import type { BusinessListingApplicationListItemResponse } from "../../types/businessListingTypes";
import { apiErrorToMessage } from "../../utils/ApiUtils";
import { formatDateTime } from "../../utils/DateUtils";
import BusinessListingApplicationMessagesPanel from "./BusinessListingApplicationMessagesPanel";
import { generateApplicantsPdf } from "./businessListingApplicantsPdf";
import Pagination from "../gigs/Pagination";
import Button from "../ui/Button/Button";
import Modal from "../ui/Modal/Modal";

type BusinessListingApplicantsSectionProps = {
  listingId: string;
  listingTitle: string;
  applicantActionsOpen: boolean;
};

const PAGE_SIZE_OPTIONS = [5, 10, 20] as const;
type ApplicantStatusFilter = "ACTIVE" | "REJECTED";
type ResumeFilter = "ALL" | "HAS_RESUME";

const toInitials = (displayName: string) =>
  displayName
    .split(" ")
    .map((part) => part.trim()[0] ?? "")
    .join("")
    .slice(0, 2)
    .toUpperCase() || "TG";

const ApplicantDetailsContent = ({
  application,
}: {
  application: BusinessListingApplicationListItemResponse;
}) => {
  const notePreview = application.message?.trim() ? application.message : "No note added with this application.";

  return (
    <section className="min-w-0 rounded-2xl border border-myteengig-border bg-myteengig-background/60 p-5 text-center md:text-left">
      <dl className="space-y-4 text-sm text-myteengig-text-primary">
        <div>
          <dt className="text-xs font-semibold uppercase tracking-[0.18em] text-myteengig-text-muted">Applicant</dt>
          <dd className="mt-1 break-words">{application.teenDisplayName}</dd>
        </div>
        <div>
          <dt className="text-xs font-semibold uppercase tracking-[0.18em] text-myteengig-text-muted">Email</dt>
          <dd className="mt-1 break-all">{application.teenEmail ?? "No email provided."}</dd>
        </div>
        {application.teenAge !== null ? (
          <div>
            <dt className="text-xs font-semibold uppercase tracking-[0.18em] text-myteengig-text-muted">Age</dt>
            <dd className="mt-1 break-words">{application.teenAge}</dd>
          </div>
        ) : null}
        <div>
          <dt className="text-xs font-semibold uppercase tracking-[0.18em] text-myteengig-text-muted">Applied</dt>
          <dd className="mt-1 break-words">{formatDateTime(application.createdAt)}</dd>
        </div>
        <div>
          <dt className="text-xs font-semibold uppercase tracking-[0.18em] text-myteengig-text-muted">Profile description</dt>
          <dd className="mt-2 whitespace-pre-wrap break-words text-sm leading-6 text-myteengig-text-primary">
            {application.teenBio?.trim() ? application.teenBio : "No profile description added."}
          </dd>
        </div>
        <div>
          <dt className="text-xs font-semibold uppercase tracking-[0.18em] text-myteengig-text-muted">Skills</dt>
          <dd className="mt-2 flex flex-wrap justify-center gap-2 md:justify-start">
            {application.teenSkills.length > 0 ? (
              application.teenSkills.map((skill) => (
                <span
                  key={skill}
                  className="rounded-full border border-myteengig-border bg-white px-3 py-1 text-xs font-medium text-myteengig-text-primary"
                >
                  {skill}
                </span>
              ))
            ) : (
              <span className="break-words text-myteengig-text-muted">No skills added.</span>
            )}
          </dd>
        </div>
        <div>
          <dt className="text-xs font-semibold uppercase tracking-[0.18em] text-myteengig-text-muted">Application note</dt>
          <dd className="mt-2 whitespace-pre-wrap break-words text-sm leading-6 text-myteengig-text-primary">
            {notePreview}
          </dd>
        </div>
        {application.rejectionReason?.trim() ? (
          <div>
            <dt className="text-xs font-semibold uppercase tracking-[0.18em] text-myteengig-text-muted">Rejection reason</dt>
            <dd className="mt-2 whitespace-pre-wrap break-words text-sm leading-6 text-myteengig-text-primary">
              {application.rejectionReason}
            </dd>
          </div>
        ) : null}
        <div>
          <dt className="text-xs font-semibold uppercase tracking-[0.18em] text-myteengig-text-muted">Resume</dt>
          <dd className="mt-1">
            {application.resume ? (
              <a
                href={application.resume.url}
                target="_blank"
                rel="noreferrer"
                className="inline-flex font-medium text-myteengig-primary hover:underline"
              >
                {application.resume.originalFileName || "Download resume"}
              </a>
            ) : (
              <span className="break-words text-myteengig-text-muted">No resume attached.</span>
            )}
          </dd>
        </div>
      </dl>
    </section>
  );
};

const BusinessListingApplicantsSection = ({
  listingId,
  listingTitle,
  applicantActionsOpen,
}: BusinessListingApplicantsSectionProps) => {
  const [searchParams] = useSearchParams();
  const [detailsModalApplicationId, setDetailsModalApplicationId] = useState<string | null>(null);
  const [statusFilter, setStatusFilter] = useState<ApplicantStatusFilter>("ACTIVE");
  const [search, setSearch] = useState("");
  const [resumeFilter, setResumeFilter] = useState<ResumeFilter>("ALL");
  const [pageSize, setPageSize] = useState<number>(10);
  const [page, setPage] = useState(0);
  const [rejectingApplicationId, setRejectingApplicationId] = useState<string | null>(null);
  const [pendingRejectApplicationId, setPendingRejectApplicationId] = useState<string | null>(null);
  const [rejectReason, setRejectReason] = useState("");
  const [messageModalApplicationId, setMessageModalApplicationId] = useState<string | null>(null);
  const [actionErrorApplicationId, setActionErrorApplicationId] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const [isDownloadModalOpen, setIsDownloadModalOpen] = useState(false);
  const [isGeneratingPdf, setIsGeneratingPdf] = useState(false);
  const [downloadError, setDownloadError] = useState<string | null>(null);

  const applicationsQuery = useQuery({
    queryKey: ["business-listing-applications", listingId],
    queryFn: () => businessListingsApi.listApplicationsForBusiness(listingId),
    refetchInterval: 15000,
  });

  const applications = useMemo(
    () => applicationsQuery.data ?? [],
    [applicationsQuery.data],
  );
  const activeApplications = useMemo(
    () => applications.filter((application) => application.status === "APPLIED"),
    [applications],
  );
  const rejectedApplications = useMemo(
    () => applications.filter((application) => application.status === "REJECTED"),
    [applications],
  );
  const filteredApplications = useMemo(
    () => {
      const statusScoped = statusFilter === "ACTIVE" ? activeApplications : rejectedApplications;
      const normalizedSearch = search.trim().toLowerCase();

      return statusScoped.filter((application) => {
        if (resumeFilter === "HAS_RESUME" && !application.resume) {
          return false;
        }
        if (!normalizedSearch) {
          return true;
        }
        return application.teenDisplayName.toLowerCase().includes(normalizedSearch);
      });
    },
    [activeApplications, rejectedApplications, resumeFilter, search, statusFilter],
  );

  const pageCount = Math.ceil(filteredApplications.length / pageSize);
  const safePage = pageCount === 0 ? 0 : Math.min(page, pageCount - 1);
  const paginatedApplications = filteredApplications.slice(
    safePage * pageSize,
    safePage * pageSize + pageSize,
  );
  const hasPreviousPage = safePage > 0;
  const hasNextPage = safePage + 1 < pageCount;

  useEffect(() => {
    setPage(0);
  }, [pageSize, statusFilter, resumeFilter, search]);

  useEffect(() => {
    if (!detailsModalApplicationId) {
      return;
    }
    if (!filteredApplications.some((application) => application.applicationId === detailsModalApplicationId)) {
      setDetailsModalApplicationId(null);
    }
  }, [filteredApplications, detailsModalApplicationId]);

  useEffect(() => {
    const applicationId = searchParams.get("applicationId");
    if (!applicationId) {
      return;
    }
    const targetApplication = applications.find((application) => application.applicationId === applicationId);
    if (!targetApplication) {
      return;
    }
    setStatusFilter(targetApplication.status === "REJECTED" ? "REJECTED" : "ACTIVE");
    if (
      applicantActionsOpen
      && searchParams.get("section") === "messages"
      && targetApplication.status === "APPLIED"
    ) {
      setMessageModalApplicationId(targetApplication.applicationId);
      return;
    }
    setDetailsModalApplicationId(targetApplication.applicationId);
  }, [applicantActionsOpen, applications, searchParams]);

  const pendingRejectApplication = applications.find(
    (application) => application.applicationId === pendingRejectApplicationId,
  ) ?? null;
  const detailsModalApplication = applications.find(
    (application) => application.applicationId === detailsModalApplicationId,
  ) ?? null;
  const messageModalApplication = applications.find(
    (application) => application.applicationId === messageModalApplicationId,
  ) ?? null;
  const filteredApplicationsWithResumesCount = filteredApplications.filter((application) => application.resume).length;

  const handleRejectApplicant = async (applicationId: string) => {
    setRejectingApplicationId(applicationId);
    setActionErrorApplicationId(applicationId);
    setActionError(null);
    try {
      await businessListingsApi.removeApplicant(applicationId, rejectReason);
      await applicationsQuery.refetch();
      setActionErrorApplicationId(null);
      setPendingRejectApplicationId(null);
      setRejectReason("");
    } catch (requestError) {
      setActionError(apiErrorToMessage(requestError, "Failed to reject applicant."));
    } finally {
      setRejectingApplicationId(null);
    }
  };

  const handleDownloadApplicantsPdf = async (includeResumes: boolean) => {
    setIsGeneratingPdf(true);
    setDownloadError(null);
    try {
      const { bytes, fileName } = await generateApplicantsPdf({
        listingTitle,
        applications: filteredApplications,
        statusFilter,
        resumeFilter,
        search,
        includeResumes,
      });
      const blobPart = bytes.buffer.slice(bytes.byteOffset, bytes.byteOffset + bytes.byteLength) as ArrayBuffer;
      const blob = new Blob([blobPart], { type: "application/pdf" });
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = fileName;
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(url);
      setIsDownloadModalOpen(false);
    } catch {
      setDownloadError("Unable to generate the applicant PDF. Please try again.");
    } finally {
      setIsGeneratingPdf(false);
    }
  };

  if (applicationsQuery.isLoading) {
    return (
      <div className="mt-5 rounded-2xl border border-myteengig-border bg-myteengig-background px-4 py-4 text-sm text-myteengig-text-muted">
        Loading applicants...
      </div>
    );
  }

  if (applicationsQuery.error) {
    return (
      <div className="mt-5 rounded-2xl border border-rose-200 bg-rose-50 px-4 py-4 text-sm text-rose-700">
        {apiErrorToMessage(applicationsQuery.error, "Failed to load applicants.")}
      </div>
    );
  }

  if (applications.length === 0) {
    return (
      <div className="mt-5 rounded-2xl border border-myteengig-border bg-myteengig-background px-4 py-4 text-sm text-myteengig-text-muted">
        No applicants yet.
      </div>
    );
  }

  return (
    <section className="mt-5 rounded-2xl border border-myteengig-border bg-myteengig-background p-4">
      <Modal
        isOpen={pendingRejectApplication !== null}
        onClose={() => {
          if (!rejectingApplicationId) {
            setPendingRejectApplicationId(null);
            setRejectReason("");
          }
        }}
        title="Reject Applicant?"
        panelClassName="max-w-lg"
      >
        <div className="space-y-4">
          <div className="rounded-2xl border border-myteengig-border bg-myteengig-background/60 p-4">
            <p className="text-xs font-semibold uppercase tracking-[0.18em] text-myteengig-text-muted">Applicant</p>
            <p className="mt-1 text-sm font-semibold text-myteengig-text-primary">
              {pendingRejectApplication?.teenDisplayName ?? "This applicant"}
            </p>
          </div>
          <p className="text-sm leading-6 text-myteengig-text-muted">
            Are you sure you want to reject this applicant? They will no longer appear in your active applicants list for this listing.
          </p>
          <div>
            <label htmlFor="reject-applicant-reason" className="mb-1 block text-sm font-medium text-myteengig-text-primary">
              Rejection reason (optional)
            </label>
            <textarea
              id="reject-applicant-reason"
              value={rejectReason}
              onChange={(event) => setRejectReason(event.target.value)}
              maxLength={1000}
              rows={4}
              className="w-full rounded-2xl border border-myteengig-border bg-white px-4 py-3 text-sm text-myteengig-text-primary outline-none transition focus:border-myteengig-primary"
              placeholder="Add context for why this applicant is not moving forward."
            />
          </div>
          <div className="flex flex-wrap items-center justify-end gap-3 pt-2">
            <Button
              type="button"
              variant="outline"
              disabled={rejectingApplicationId !== null}
              onClick={() => setPendingRejectApplicationId(null)}
            >
              Cancel
            </Button>
            <Button
              type="button"
              disabled={!pendingRejectApplication || rejectingApplicationId !== null}
              onClick={() => {
                if (pendingRejectApplication) {
                  void handleRejectApplicant(pendingRejectApplication.applicationId);
                }
              }}
            >
              {rejectingApplicationId !== null ? "Rejecting..." : "Yes, Reject Applicant"}
            </Button>
          </div>
        </div>
      </Modal>
      <Modal
        isOpen={detailsModalApplication !== null}
        onClose={() => setDetailsModalApplicationId(null)}
        title={detailsModalApplication ? `Applicant Details: ${detailsModalApplication.teenDisplayName}` : "Applicant Details"}
        panelClassName="max-w-4xl h-[min(90vh,52rem)]"
        bodyClassName="overflow-y-auto"
      >
        {detailsModalApplication ? <ApplicantDetailsContent application={detailsModalApplication} /> : null}
      </Modal>
      <Modal
        isOpen={messageModalApplication !== null}
        onClose={() => setMessageModalApplicationId(null)}
        title={messageModalApplication ? `Messages with ${messageModalApplication.teenDisplayName}` : "Messages"}
        panelClassName="max-w-5xl h-[min(90vh,52rem)]"
        bodyClassName="h-full"
      >
        {messageModalApplication ? (
          <BusinessListingApplicationMessagesPanel
            applicationId={messageModalApplication.applicationId}
            role="business"
            participantLabel={messageModalApplication.teenDisplayName}
            description={`Message ${messageModalApplication.teenDisplayName} here to discuss fit, timing, and next steps.`}
            variant="bare"
            fullHeight
          />
        ) : null}
      </Modal>
      <Modal
        isOpen={isDownloadModalOpen}
        onClose={() => {
          if (!isGeneratingPdf) {
            setIsDownloadModalOpen(false);
            setDownloadError(null);
          }
        }}
        title="Download Applicant PDF"
        panelClassName="max-w-lg"
      >
        <div className="space-y-4">
          <p className="text-sm leading-6 text-myteengig-text-muted">
            Download a PDF for the {filteredApplications.length} applicant{filteredApplications.length === 1 ? "" : "s"} currently matching your filters.
          </p>
          <div className="rounded-2xl border border-myteengig-border bg-myteengig-background/60 p-4 text-sm text-myteengig-text-primary">
            <p className="font-semibold">Include resumes?</p>
            <p className="mt-1 text-myteengig-text-muted">
              {filteredApplicationsWithResumesCount} matching applicant{filteredApplicationsWithResumesCount === 1 ? " has" : "s have"} a resume. PDF and image resumes will be added as separate pages beneath the applicant details.
            </p>
          </div>
          {downloadError ? <p className="text-sm text-rose-700">{downloadError}</p> : null}
          <div className="flex flex-wrap items-center justify-end gap-3 pt-2">
            <Button
              type="button"
              variant="outline"
              disabled={isGeneratingPdf}
              onClick={() => void handleDownloadApplicantsPdf(false)}
            >
              {isGeneratingPdf ? "Generating..." : "No, PDF Only"}
            </Button>
            <Button
              type="button"
              disabled={isGeneratingPdf}
              onClick={() => void handleDownloadApplicantsPdf(true)}
            >
              {isGeneratingPdf ? "Generating..." : "Yes, Include Resumes"}
            </Button>
          </div>
        </div>
      </Modal>

      <div className="flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
        <div>
          <h3 className="text-sm font-semibold text-myteengig-text-primary">
            Applicants ({filteredApplications.length})
          </h3>
          <p className="mt-1 text-sm text-myteengig-text-muted">
            {applicantActionsOpen
              ? "Review active applicants and message them back and forth for any information needed to continue your application process."
              : "Review applicant details here. This listing is no longer active, so messaging and rejection are unavailable."}
          </p>
        </div>
        <div className="min-w-0 flex flex-col items-stretch gap-3 sm:flex-row sm:flex-wrap sm:items-end">
          <div className="min-w-0 w-full sm:w-auto sm:min-w-[15rem] sm:flex-1 lg:min-w-[18rem]">
            <label htmlFor="business-applicant-search" className="mb-1 block text-xs font-semibold uppercase tracking-wide text-myteengig-text-muted">
              Search
            </label>
            <input
              id="business-applicant-search"
              type="text"
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              placeholder="Search by teen name"
              className="h-[42px] w-full min-w-0 rounded-lg border border-myteengig-border bg-white px-3 text-sm text-myteengig-text-primary shadow-sm outline-none transition focus:border-myteengig-primary focus:ring-2 focus:ring-myteengig-primary/20"
            />
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <Button
              type="button"
              variant={statusFilter === "ACTIVE" ? "primary" : "outline"}
              onClick={() => setStatusFilter("ACTIVE")}
            >
              Active
            </Button>
            <Button
              type="button"
              variant={statusFilter === "REJECTED" ? "primary" : "outline"}
              onClick={() => setStatusFilter("REJECTED")}
            >
              Rejected
            </Button>
          </div>
          <div className="hidden min-w-0 md:block">
            <label htmlFor="business-applicants-resume-filter" className="mb-1 block text-xs font-semibold uppercase tracking-wide text-myteengig-text-muted">
              Resume
            </label>
            <select
              id="business-applicants-resume-filter"
              value={resumeFilter}
              onChange={(event) => setResumeFilter(event.target.value as ResumeFilter)}
              className="h-[42px] rounded-lg border border-myteengig-border bg-white px-3 text-sm font-medium text-myteengig-text-primary shadow-sm outline-none transition focus:border-myteengig-primary focus:ring-2 focus:ring-myteengig-primary/20"
            >
              <option value="ALL">All</option>
              <option value="HAS_RESUME">Has Resume</option>
            </select>
          </div>
          <div className="hidden min-w-0 md:block">
            <label htmlFor="business-applicants-page-size" className="mb-1 block text-xs font-semibold uppercase tracking-wide text-myteengig-text-muted">
              Rows
            </label>
            <select
              id="business-applicants-page-size"
              value={pageSize}
              onChange={(event) => setPageSize(Number(event.target.value))}
              className="h-[42px] rounded-lg border border-myteengig-border bg-white px-3 text-sm font-medium text-myteengig-text-primary shadow-sm outline-none transition focus:border-myteengig-primary focus:ring-2 focus:ring-myteengig-primary/20"
            >
              {PAGE_SIZE_OPTIONS.map((option) => (
                <option key={option} value={option}>
                  {option}
                </option>
              ))}
            </select>
          </div>
          <Button
            type="button"
            variant="outline"
            disabled={filteredApplications.length === 0}
            onClick={() => {
              setDownloadError(null);
              setIsDownloadModalOpen(true);
            }}
            className="h-[42px]"
          >
            Download PDF
          </Button>
        </div>
      </div>

      <div className="mt-4 space-y-4">
        {!applicantActionsOpen ? (
          <div className="rounded-2xl border border-myteengig-border bg-white px-4 py-4 text-sm text-myteengig-text-muted">
            This listing is no longer active. Applicant details remain visible here, but messaging and rejection are unavailable.
          </div>
        ) : null}
        {paginatedApplications.length === 0 ? (
          <div className="rounded-2xl border border-myteengig-border bg-white px-4 py-4 text-sm text-myteengig-text-muted">
            {statusFilter === "ACTIVE" ? "No active applicants right now." : "No rejected applicants right now."}
          </div>
        ) : null}

        {paginatedApplications.map((application) => {
          const notePreview = application.message?.trim() ? application.message : "No note added with this application.";
          const canMessage = application.status === "APPLIED";

          return (
            <article key={application.applicationId} className="rounded-2xl border border-myteengig-border bg-white px-4 py-4 shadow-sm">
              <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
                <div className="min-w-0">
                  <div className="flex flex-col items-center gap-3 text-center lg:flex-row lg:items-center lg:text-left">
                    <div className="flex h-14 w-14 shrink-0 items-center justify-center overflow-hidden rounded-full border border-myteengig-border bg-myteengig-background text-sm font-semibold text-myteengig-text-primary">
                      {application.teenProfilePhoto ? (
                        <img src={application.teenProfilePhoto.url} alt={`${application.teenDisplayName} profile`} className="h-full w-full object-cover" />
                      ) : (
                        toInitials(application.teenDisplayName)
                      )}
                    </div>
                    <div className="min-w-0">
                      <div className="flex flex-wrap items-center justify-center gap-2 lg:justify-start">
                        <p className="text-base font-semibold text-myteengig-text-primary">{application.teenDisplayName}</p>
                        {applicantActionsOpen && application.hasUnreadMessages ? (
                          <span className="rounded-full border border-myteengig-primary/30 bg-myteengig-background px-2 py-0.5 text-[11px] font-semibold text-myteengig-primary">
                            New Message
                          </span>
                        ) : null}
                      </div>
                      <p className="mt-1 text-xs text-myteengig-text-muted">
                        Applied {formatDateTime(application.createdAt)}
                        {application.teenAge !== null ? ` • Age ${application.teenAge}` : ""}
                      </p>
                    </div>
                  </div>
                  <p className="mt-3 line-clamp-3 whitespace-pre-wrap break-words text-sm leading-6 text-myteengig-text-primary">
                    {notePreview}
                  </p>
                </div>

                <div className="flex flex-wrap items-center justify-center gap-2 lg:max-w-[20rem] lg:justify-end">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => setDetailsModalApplicationId(application.applicationId)}
                  >
                    View Details
                  </Button>
                  {canMessage && applicantActionsOpen ? (
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => setMessageModalApplicationId(application.applicationId)}
                    >
                      View Messages
                    </Button>
                  ) : null}
                  {canMessage && applicantActionsOpen ? (
                    <Button
                      type="button"
                      variant="outline"
                      disabled={rejectingApplicationId === application.applicationId}
                      onClick={() => {
                        setActionError(null);
                        setRejectReason("");
                        setPendingRejectApplicationId(application.applicationId);
                      }}
                    >
                      {rejectingApplicationId === application.applicationId ? "Rejecting..." : "Reject Applicant"}
                    </Button>
                  ) : null}
                </div>
              </div>

              {actionError && actionErrorApplicationId === application.applicationId ? (
                <p className="mt-3 text-sm text-rose-700">{actionError}</p>
              ) : null}
            </article>
          );
        })}
      </div>

      <Pagination
        page={safePage}
        pageSize={pageSize}
        hasNextPage={hasNextPage}
        hasPreviousPage={hasPreviousPage}
        resultsCount={paginatedApplications.length}
        onPageChange={setPage}
      />
    </section>
  );
};

export default BusinessListingApplicantsSection;
